const euMembersWithoutEuroIsoAlpha2 = {
    BG: 'BG',
    CZ: 'CZ',
    HU: 'HU',
    PL: 'PL',
    RO: 'RO',
    DK: 'DK',
    SE: 'SE',
};

const euMembersWithEuroIsoAlpha2 = {
    BE: 'BE',
    DE: 'DE',
    EE: 'EE',
    IE: 'IE',
    EL: 'EL',
    ES: 'ES',
    FR: 'FR',
    HR: 'HR',
    IT: 'IT',
    CY: 'CY',
    LV: 'LV',
    LT: 'LT',
    LU: 'LU',
    MT: 'MT',
    NL: 'NL',
    AT: 'AT',
    PT: 'PT',
    SI: 'SI',
    SK: 'SK',
    FI: 'FI',
};

const euMembersIsoAlpha2 = {
    ...euMembersWithEuroIsoAlpha2,
    ...euMembersWithoutEuroIsoAlpha2,
};

const eeaMembersIsoAlpha2 = {
    IS: 'IS',
    LI: 'LI',
    NO: 'NO',
};

/**
 * Azores (Portuguese territory)
 * Ceuta and Melilla (Spanish autonomous cities)
 * French Guiana (overseas department and region of France)
 * Guadeloupe (overseas department and region of France)
 * Canary Islands (Spanish autonomous community)
 * Madeira (Portuguese autonomous region)
 * Martinique (overseas department and region of France)
 * Mayotte (overseas department and region of France)
 * Réunion (overseas department and region of France)
 * Saint Barthélemy (overseas collectivity of France)
 * Saint Pierre and Miquelon (overseas collectivity of France)
 */
const euOverseasTerritoryIsoAlpha2 = {
    'PT-20': 'PT-20',
    'ES-CE': 'ES-CE',
    'ES-ML': 'ES-ML',
    GF: 'GF',
    GP: 'GP',
    'ES-CN': 'ES-CN',
    'PT-30': 'PT-30',
    MQ: 'MQ',
    YT: 'YT',
    RE: 'RE',
    BL: 'BL',
    PM: 'PM',
};

/**
 * Andorra (not an EU member)
 * Kosovo (not an EU member)
 * Monaco (not an EU member)
 * Montenegro (not an EU member)
 * San Marino (not an EU member)
 * Vatican City (not an EU member)
 */
const notEuropeanUnionMembersWithEuro = {
    AD: 'AD',
    XK: 'XK',
    MC: 'MC',
    ME: 'ME',
    MS: 'SM',
    VA: 'VA',
};

const isEuMember = (isoAlpha2Code) => {
    return Object.keys(euMembersIsoAlpha2).indexOf(isoAlpha2Code.toUpperCase()) != -1;
};

const isCountryUseEuro = (isoAlpha2Code) => {
    const euMembersWithEuroIsoAlpha2Array = Object.keys(euMembersWithEuroIsoAlpha2);
    const allCountriesWithEuro = euMembersWithEuroIsoAlpha2Array
        .concat(Object.keys(euOverseasTerritoryIsoAlpha2))
        .concat(Object.keys(notEuropeanUnionMembersWithEuro));

    return Object.keys(allCountriesWithEuro).indexOf(isoAlpha2Code.toUpperCase()) != -1;
};

// https://euipo.europa.eu/tunnel-web/secure/webdav/guest/document_library/Documents/COSME/VAT%20numbers%20EU.pdf
// https://docs.umbrella.com/cloudlock-documentation/docs/eu-personal-identifiers
const getVATIdentificationNumberInfo = (isoAlpha2Code) => {
    const countryCode = isoAlpha2Code.toUpperCase();

    const vatInfo = {
        AT: {
            format: 'U+8 digits',
            structure: 'ATU99999999',
            localName: 'Umsatzsteuer-Identifikationsnummer',
            abbreviation: 'UID',
        },
        BE: {
            format: '10 digits',
            structure: 'BE0999999999/BE1999999999',
            localName: 'BTW identificatienummer / Numéro de TVA',
            abbreviation: 'n° TVA, BTW-nr, Mwst-nr',
        },
        BG: {
            format: '9–10 digits',
            structure: 'BG999999999/BG9999999999',
            localName: 'Identifikacionen nomer po DDS',
            abbreviation: 'ДДС номер',
        },
        HR: {
            format: '11 digits',
            structure: 'HR99999999999',
            localName: 'PDV Id. Broj OIB',
            abbreviation: 'PDV-ID; OIB',
        },
        CY: {
            format: '9 characters',
            structure: 'CY99999999L',
            localName: 'Arithmós Engraphḗs phi. pi. a.',
            abbreviation: 'ΦΠΑ',
        },
        CZ: {
            format: '8-10 digits',
            structure: 'CZ99999999',
            localName: 'Daňové identifikační číslo',
            abbreviation: 'DIČ',
        },
        DK: {
            format: '8 digits',
            structure: 'DK99999999',
            localName: 'Momsregistreringsnummer',
            abbreviation: 'CVR',
        },
        EE: {
            format: '9 digits',
            structure: 'EE999999999',
            localName: 'Käibemaksukohustuslase number',
            abbreviation: 'KMKR',
        },
        FI: {
            format: '8 digits',
            structure: 'FI99999999',
            localName: 'Arvonlisäveronumero Mervärdesskattenummer',
            abbreviation: 'ALV nro Momsnummer',
        },
        FR: {
            format: '2 characters + 9 digits',
            structure: 'FRXX 999999999',
            localName: "Numéro d'identification à la taxe sur la valeur ajoutée / Numéro de TVA intracommunautaire",
            abbreviation: 'n° TVA',
        },
        DE: {
            format: '9 digits',
            structure: 'DE999999999',
            localName: 'UmsatzsteuerIdentifikationsnummer',
            abbreviation: 'USt-IdNr.',
        },
        EL: {
            format: '9 digits',
            structure: 'EL999999999',
            localName: 'Arithmós Forologikou Mētrṓou',
            abbreviation: 'ΑΦΜ',
        },
        HU: {
            format: '8 digits',
            structure: 'HU99999999',
            localName: 'Közösségi adószám',
            abbreviation: 'ANUM',
        },
        IE: {
            format: '8-9 characters',
            structure: 'IE9999999WI',
            localName: 'Value added tax identification no.',
            abbreviation: 'VAT or CBL',
        },
        IT: {
            format: '11 digits',
            structure: 'IT99999999999',
            localName: 'Partita IVA (IVA = Imposta sul Valore Aggiunto)',
            abbreviation: 'P.IVA',
        },
        LV: {
            format: '11 digits',
            structure: 'LV99999999999',
            localName: 'Pievienotās vērtības nodokļa (PVN) reģistrācijas numurs',
            abbreviation: 'PVN',
        },
        LT: {
            format: '9 or 12 digits',
            structure: 'LT999999999999',
            localName: 'PVM (abbrev. Pridėtinės vertės mokestis) mokėtojo kodas',
            abbreviation: 'PVM kodas',
        },
        LU: {
            format: '8 digits',
            structure: 'LU99999999',
            localName: "Numéro d'identification à la taxe sur la valeur ajoutée",
            abbreviation: 'No. TVA',
        },
        MT: {
            format: '8 digits',
            structure: 'LU99999999',
            localName: 'Vat reg. no.',
            abbreviation: 'Vat No.',
        },
        PL: {
            format: '10 digits',
            structure: 'PL9999999999',
            localName: 'Numer identyfikacji podatkowej',
            abbreviation: 'NIP',
        },
        PT: {
            format: '9 digits',
            structure: 'PT999999999',
            localName: 'Número de Identificação Fiscal (NIF)',
            abbreviation: 'NIF',
        },
        RO: {
            format: 'From 2 digits to 10 digits',
            structure: 'RO99********',
            localName: 'Codul de identificare fiscală',
            abbreviation: 'CIF',
        },
        SK: {
            format: '10 digits',
            structure: 'SK9999999999',
            localName: 'Identifikačné číslo pre daň z pridanej hodnoty',
            abbreviation: 'IČ DPH',
        },
        SI: {
            format: '8 digits',
            structure: 'SI99999999',
            localName: 'Identifikacijska številka za DDV ',
            abbreviation: 'ID za DDV',
        },
        ES: {
            format: '9 characters (The first and last characters may be alpha or numeric; but they may not both be',
            structure: 'ESX9999999X',
            localName: 'Número de Identificación Fiscal (formerly named Código de Identificación Fiscal)',
            abbreviation: 'NIF (CIF)',
        },
        SE: {
            format: '12 digits',
            structure: 'SE999999999999',
            localName: 'VAT-nummer or momsnummer or momsregistreringsnummer',
            abbreviation: 'Momsnr.',
        },
        NL: {
            format: '12 characters (9 digits+B+2-digit)',
            structure: 'NLSSSSSSSSSSSS',
            localName: 'Btw-nummer',
            abbreviation: 'Btw-nr.',
        },
        UA: {
            format: '10 digits',
            structure: '9999999999',
            localName: 'Реєстраційний номер облікової картки платника податків',
            abbreviation: 'РНОКПП',
        },
    };

    return (
        vatInfo[countryCode] || {
            format: 'Optional: Provide numbers used for tax identification in your country',
            structure: 'Format and structure depend on local tax regulations',
            localName: 'VAT Identification Number',
            abbreviation: 'VAT',
        }
    );
};

const getTaxIdentificationNumberLocalName = (isoAlpha2Code) => {
    const countryCode = isoAlpha2Code.toUpperCase();

    const taxIdLocalNames = {
        AT: {
            abbreviation: 'Steuernummer/Firmenbuchnummer',
        },
        BE: {
            abbreviation: "Ondernemingsnummer / Numéro d'entreprise",
        },
        BG: {
            abbreviation: 'Булстат Bulstat Code Единен идентификационен код (ЕИК/ПИК)',
        },
        HR: {
            abbreviation: 'Matični broj subjekta (MBS)',
        },
        CY: {
            abbreviation: 'Αριθμός Εγγραφής',
        },
        CZ: {
            abbreviation: 'Identifikační číslo (IČO)',
        },
        DK: {
            abbreviation: 'Det centrale virksomhedsregister (CVR-nummer)',
        },
        EE: {
            abbreviation: 'Registrikood',
        },
        FI: {
            abbreviation: 'Yritys-ja yhteisotunnus (Y-tunnus). Företags- och organisationsnummer (FO-nummer)',
        },
        FR: {
            abbreviation:
                'Immatriculation au Registre de Commerce et des Sociétés (RCS)/Système Informatique du Répertoire des Entreprises (SIRENE)',
        },
        DE: {
            abbreviation: 'Steuernummer - Identifikationsnummer (IdNr)',
        },
        EL: {
            abbreviation: 'Geniko Emboriko Mitroo (G.Ε.ΜI.): ΑΡΙΘΜΟΣ Γ.Ε.ΜΗ',
        },
        HU: {
            abbreviation: 'Cégjegyzékszám',
        },
        IE: {
            abbreviation: 'Main registration number',
        },
        IT: {
            abbreviation: 'Repertorio Economico Amministrativo (REA) / Codice fiscale',
        },
        LV: {
            abbreviation: 'Vienotais reģistrācijas numurus',
        },
        LT: {
            abbreviation: 'Kodas',
        },
        LU: {
            abbreviation: 'Numéro d’immatriculation Handelsregisternummer - Registre de commerce et des sociétés RCS ',
        },
        MT: {
            abbreviation: "Registration number / Numru taċ-Ċertifikat ta' Reġistrazzjoni",
        },
        PL: {
            abbreviation: 'REGON',
        },
        PT: {
            abbreviation: 'Número de identificação de pessoa colectiva (NIPC)',
        },
        RO: {
            abbreviation: 'Cod Unic de Inregistrare(CUI) / Numar de ordine in registrul comertului',
        },
        SK: {
            abbreviation: 'Identifikačné číslo (ICO)',
        },
        SI: {
            abbreviation: 'IMatična številka',
        },
        ES: {
            abbreviation: 'Registro Mercantil',
        },
        SE: {
            abbreviation: 'Organisationsnummer',
        },
        NL: {
            abbreviation: 'Kamer van Koophandel (KvK-nummer) / Dossiernummer',
        },
        UA: {
            abbreviation: 'Індивідуальний податковий номер (ІПН)',
        },
    };

    return (
        taxIdLocalNames[countryCode] || {
            abbreviation: 'Tax Identification Number',
        }
    );
};

module.exports = {
    isEuMember,
    isCountryUseEuro,
    getVATIdentificationNumberInfo,
    getTaxIdentificationNumberLocalName,
};
